// Case Tag customer scoping — shared by the Case Tags admin page, the delete-with-replace
// dialog, and the Customer Specs Case Tag picker.
//
// A tag's scope lives in dbo.CaseTags.CustomersCsv, the same sentinel-or-CSV shape as
// dbo.BagLabels.CustomersCsv: 'All' (or blank) = a universal tag offered to every customer,
// otherwise a CSV of dbo.Customers.UniqueID. Keep ALL_CUSTOMERS_VALUE in sync with the
// backend sentinel (api-server/routes/CaseTags.js ALL_CUSTOMERS).
export const ALL_CUSTOMERS_VALUE = 'All';

// A tag with no specific customers is an "All Customers" (universal) tag.
export function isAllCustomersTag(ct) {
  if (!ct) return false;
  const s = String(ct.CustomersCsv ?? '').trim();
  return s === '' || s === ALL_CUSTOMERS_VALUE;
}

// The specific customer ids a tag is scoped to; empty for a universal tag.
export function caseTagCustomerIds(ct) {
  if (!ct || isAllCustomersTag(ct)) return [];
  return String(ct.CustomersCsv)
    .split(',')
    .map((t) => Number(t.trim()))
    .filter((n) => Number.isInteger(n) && n > 0);
}

// Whether a tag is offered to a given customer: universal tags apply to everyone,
// specific tags only to the customers named in their CSV.
export function caseTagAppliesTo(ct, customerId) {
  const custId = Number(customerId) || 0;
  if (isAllCustomersTag(ct)) return true;
  if (custId <= 0) return false;
  return caseTagCustomerIds(ct).includes(custId);
}

// Display labels for a tag's scope — one pill each. Unresolvable ids fall back to '#id'
// so a stale reference is still visible rather than silently dropped.
export function caseTagScopeLabels(ct, customerNameById) {
  if (isAllCustomersTag(ct)) return ['All Customers'];
  return caseTagCustomerIds(ct).map((id) => {
    const name = customerNameById?.get?.(String(id));
    return name || `#${id}`;
  });
}

// Options for a spec's Case Tag picker, scoped by the spec's customer (union semantics):
// the customer's own tags AND the universal "All Customers" tags are both offered, so a
// customer with private tags still sees the shared KRP ones.
// The currently-selected tag is always kept in the list — even when it no longer matches
// the scope — so TypeaheadSelect renders its real name instead of the placeholder.
export function caseTagOptionsForCustomer(caseTags, customerId, selectedId) {
  const list = Array.isArray(caseTags) ? caseTags : [];
  const base = list.filter((ct) => caseTagAppliesTo(ct, customerId));

  const sel = selectedId == null ? '' : String(selectedId);
  let chosen = base;
  if (sel && !base.some((ct) => String(ct.UniqueID) === sel)) {
    const current = list.find((ct) => String(ct.UniqueID) === sel);
    if (current) chosen = [current, ...base];
  }
  return chosen.map((ct) => ({ value: String(ct.UniqueID), label: String(ct.CaseTagName || '') }));
}
